// The opponents.
//
// Every choice is expected value over EXACT leg odds (engine/odds.js - the
// same movement code the dispatch path runs), plus seeded Monte Carlo for
// the one question a leg can't answer alone: who wins the whole race.
//
// Three temperaments differ in one knob, `nerve` - how much they discount
// the safe guaranteed pound of rolling the pyramid against the gamble of a
// bet. The cant-stop lesson about style-vs-strength axes is honoured the
// cheap way here: the knob scales BOTH directions of variance (a nervier
// bot both bets earlier on winners and takes cheekier leg tiles), so it
// changes flavour more than it changes win rate; nobody has run a 600-game
// sweep for a betting party game and nobody should.

import { legOdds } from '../odds';
import { moveCamel, rankCamels, raceWon, legalDesertSpaces } from '../moves';
import {
  CAMELS, DIE_MIN, DIE_MAX, FINAL_BET_PAYOUTS, FINAL_BET_CONSOLATION,
} from '../constants';
import { nextInt, nextRandom } from '../rng/rng';
import {
  takeLegTileAction, placeDesertAction, rollAction, betAction,
} from '../actions/turnActions';

export const PERSONALITIES = {
  steady: { label: 'Steady', nerve: 1.0 },
  jumpy: { label: 'Jumpy', nerve: 1.35 },
  cool: { label: 'Cool', nerve: 0.75 },
};
export const personalityFor = (index) => ['steady', 'jumpy', 'cool'][index % 3];

const ROLLOUTS = 160;

/**
 * P(camel wins the race), by seeded rollout: play random dice to the end of
 * the race through the real movement code. Desert tiles are ignored beyond
 * the current leg (they come back somewhere unknowable anyway).
 */
export const raceWinOdds = (track, legDice, desert, rng) => {
  const wins = Object.fromEntries(CAMELS.map((c) => [c, 0]));
  const losses = Object.fromEntries(CAMELS.map((c) => [c, 0]));
  let state = rng;
  for (let i = 0; i < ROLLOUTS; i += 1) {
    let t = track;
    let dice = legDice.slice();
    let tiles = desert;
    let guard = 0;
    while (!raceWon(t) && guard++ < 60) {
      if (dice.length === 0) { dice = CAMELS.slice(); tiles = {}; }
      let di; let fv;
      [di, state] = nextInt(dice.length, state);
      [fv, state] = nextInt(DIE_MAX - DIE_MIN + 1, state);
      const camel = dice[di];
      dice = dice.slice(0, di).concat(dice.slice(di + 1));
      ({ track: t } = moveCamel(t, camel, fv + DIE_MIN, tiles));
    }
    const ranked = rankCamels(t);
    wins[ranked[0]] += 1 / ROLLOUTS;
    losses[ranked[ranked.length - 1]] += 1 / ROLLOUTS;
  }
  return { win: wins, lose: losses, rng: state };
};

/**
 * The bot's turn. Scores all four actions in expected pounds and takes the
 * best, with a whisper of seeded noise so equal options vary.
 * Uses PUBLIC information only: it never reads the hidden camels on
 * anyone's placed bet cards, including its own past ones.
 */
export const decideAction = (state, seat, rng, personalityKey = 'steady') => {
  const { nerve } = PERSONALITIES[personalityKey] || PERSONALITIES.steady;
  const player = state.players[seat];
  const odds = legOdds(state.track, state.legDice, state.desert);
  const candidates = [];

  // 1. Leg tiles: lead pays the tile, second pays 1, wrong costs 1 (p4).
  CAMELS.forEach((camel) => {
    const pile = state.legTiles[camel];
    if (!pile || pile.length === 0) return;
    const value = pile[0];
    const pL = odds.pLeader[camel];
    const pS = odds.pSecond[camel];
    const ev = (pL * value + pS * 1 - (1 - pL - pS) * 1) * nerve;
    candidates.push({ ev, action: takeLegTileAction(camel) });
  });

  // 2. The pyramid: a guaranteed pound (p3), and it moves the race along.
  candidates.push({ ev: 1.0, action: rollAction() });

  // 3. The desert tile: worth the chance a unit lands on it this leg.
  const spaces = legalDesertSpaces(state.track, state.desert, seat);
  let bestSpace = null;
  spaces.forEach((space) => {
    const ev = odds.landings[space] || 0;
    if (!bestSpace || ev > bestSpace.ev) bestSpace = { space, ev };
  });
  if (bestSpace && (!state.desert[seat] || bestSpace.ev > (odds.landings[state.desert[seat].space] || 0) + 0.15)) {
    // Mirage ahead of the pack, oasis behind it - a coarse flavour call
    // that costs nothing: the trigger pound is the same either side.
    candidates.push({
      ev: bestSpace.ev,
      action: placeDesertAction(bestSpace.space, bestSpace.space >= 8 ? 'mirage' : 'oasis'),
    });
  }

  // 4. Final bets, when the race has taken enough shape to say anything.
  const cardsLeft = CAMELS.filter((c) => player.betCards[c]);
  if (cardsLeft.length && (state.turn.leg >= 2 || odds.pRaceEnds > 0)) {
    const race = raceWinOdds(state.track, state.legDice, state.desert, rng);
    rng = race.rng;
    for (const camel of cardsLeft) {
      const pWin = race.win[camel];
      const winPayout = FINAL_BET_PAYOUTS[state.bets.winner.length] ?? FINAL_BET_CONSOLATION;
      const evWin = (pWin * winPayout - (1 - pWin) * 1) * nerve;
      candidates.push({ ev: evWin, action: betAction('winner', camel) });

      const pLose = race.lose[camel];
      const losePayout = FINAL_BET_PAYOUTS[state.bets.loser.length] ?? FINAL_BET_CONSOLATION;
      const evLose = (pLose * losePayout - (1 - pLose) * 1) * nerve;
      candidates.push({ ev: evLose, action: betAction('loser', camel) });
    }
  }

  let best = null;
  for (const candidate of candidates) {
    let noise;
    [noise, rng] = nextRandom(rng);
    const jittered = candidate.ev + noise * 0.05;
    if (!best || jittered > best.jittered) best = { ...candidate, jittered };
  }
  return { action: best.action, rng };
};
